import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { IBusinessFull } from "../../../interfaces/businessFull.interface";
import { expandDateRange } from "../dayPlan";
import {
  formatEventRange,
  groupNamedDays,
  IEventDayRange,
  INamedDayGroup,
} from "../groupEventDays";
import {
  deleteMockHoliday,
  listMockHolidays,
  MockHoliday,
  upsertMockHoliday,
} from "../qaToolsApi";

const { RangePicker } = DatePicker;

interface Props {
  business: IBusinessFull;
}

function defaultRange(): [Dayjs, Dayjs] {
  const tomorrow = dayjs().add(1, "day");
  return [tomorrow, tomorrow];
}

export default function MockHolidaysForm({ business }: Props) {
  const [form] = Form.useForm();
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange);
  const [holidays, setHolidays] = useState<MockHoliday[]>([]);
  const [editing, setEditing] = useState<INamedDayGroup>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  const businessId = String(business._id);
  const groups = groupNamedDays(holidays);

  const refresh = async (): Promise<MockHoliday[]> => {
    setIsLoading(true);
    setError(undefined);
    try {
      const listed = await listMockHolidays(businessId);
      const next = listed.holidays ?? [];
      setHolidays(next);
      return next;
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Could not load mock holidays"
      );
      return holidays;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // businessId is the only input the list is keyed on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const closeForm = () => {
    setIsFormOpen(false);
    setEditing(undefined);
    form.resetFields();
    setRange(defaultRange());
    setError(undefined);
  };

  const startCreate = () => {
    setEditing(undefined);
    form.resetFields();
    setRange(defaultRange());
    setError(undefined);
    setIsFormOpen(true);
  };

  const startEdit = (row: INamedDayGroup) => {
    setEditing(row);
    form.setFieldsValue({ name: row.name });
    setRange(defaultRange());
    setError(undefined);
    setIsFormOpen(true);
  };

  const submit = async (values: { name: string }) => {
    const days = expandDateRange(
      range[0].format("YYYY-MM-DD"),
      range[1].format("YYYY-MM-DD")
    );
    if (!days.length) {
      setError("Pick a date or a range");
      return;
    }

    setIsSaving(true);
    setError(undefined);
    try {
      const name = editing?.name ?? values.name;
      for (const date of days) {
        await upsertMockHoliday({ businessId, date, name });
      }
      const next = await refresh();
      if (editing) {
        setEditing(
          groupNamedDays(next).find((group) => group.name === editing.name)
        );
        setRange(defaultRange());
      } else {
        closeForm();
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Could not save the mock holiday"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const removeGroup = async (row: INamedDayGroup) => {
    setError(undefined);
    try {
      for (const date of row.dates) {
        await deleteMockHoliday({ businessId, date });
      }
      if (editing?.name === row.name) closeForm();
      await refresh();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Could not remove the mock holiday"
      );
    }
  };

  const removeRange = async (row: INamedDayGroup, span: IEventDayRange) => {
    setError(undefined);
    try {
      for (const date of span.days) {
        await deleteMockHoliday({ businessId, date });
      }
      const next = await refresh();
      if (editing?.name === row.name) {
        setEditing(
          groupNamedDays(next).find((group) => group.name === row.name)
        );
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Could not remove that range"
      );
    }
  };

  if (isFormOpen) {
    const existingRanges = editing?.ranges ?? [];

    return (
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {editing ? "Edit holiday" : "Create holiday"}
        </Typography.Title>

        {error && <Alert type="error" showIcon message={error} />}

        {editing && (
          <Alert
            type="info"
            showIcon
            message={`Adding days to "${editing.name}"`}
            description="The name stays put — a new name would create a second holiday. Remove a range below, or add another."
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={submit}
          initialValues={{ name: "" }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "A holiday name is required" }]}
          >
            <Input
              placeholder="Mock Liberation Day"
              maxLength={80}
              disabled={!!editing}
            />
          </Form.Item>
          <Form.Item label="Range" required>
            <RangePicker
              value={range}
              allowClear={false}
              onChange={(next) =>
                next?.[0] && next[1] && setRange([next[0], next[1]])
              }
            />
          </Form.Item>

          {editing && (
            <Form.Item label="Existing ranges">
              {existingRanges.length ? (
                <Space size={[4, 4]} wrap>
                  {existingRanges.map((span) => (
                    <Tag
                      key={`${span.from}-${span.to}`}
                      closable
                      onClose={(event) => {
                        event.preventDefault();
                        void removeRange(editing, span);
                      }}
                    >
                      {formatEventRange(span)}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Typography.Text type="secondary">None</Typography.Text>
              )}
            </Form.Item>
          )}

          <Space>
            <Button type="primary" htmlType="submit" loading={isSaving}>
              {editing ? "Add range" : "Create holiday"}
            </Button>
            <Button onClick={closeForm}>Back to list</Button>
          </Space>
        </Form>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Space
        style={{ width: "100%", justifyContent: "space-between" }}
        align="start"
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Overlays the public holiday calendar for this merchant on non-prod. An
          upcoming day shows the forecast star and badge immediately. A past day
          needs an insights resync before the model can train on it.
        </Typography.Paragraph>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={startCreate}
          style={{ flexShrink: 0 }}
        >
          Create holiday
        </Button>
      </Space>

      {error && <Alert type="error" showIcon message={error} />}

      <Table
        size="small"
        rowKey="name"
        loading={isLoading}
        dataSource={groups}
        pagination={false}
        locale={{ emptyText: "No mock holidays on this merchant yet" }}
        columns={[
          { title: "Name", dataIndex: "name", width: 180 },
          {
            title: "Ranges",
            key: "ranges",
            render: (_unused, row) =>
              row.ranges.length ? (
                <Space direction="vertical" size={0}>
                  {row.ranges.map((span) => (
                    <Typography.Text key={`${span.from}-${span.to}`}>
                      {formatEventRange(span)}
                    </Typography.Text>
                  ))}
                </Space>
              ) : (
                <Typography.Text type="secondary">None</Typography.Text>
              ),
          },
          {
            title: "",
            key: "actions",
            width: 88,
            render: (_unused, row) => (
              <Space>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  aria-label={`Edit ${row.name}`}
                  onClick={() => startEdit(row)}
                />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Remove ${row.name}`}
                  onClick={() => void removeGroup(row)}
                />
              </Space>
            ),
          },
        ]}
      />
    </Space>
  );
}
